import {combineReducers} from 'redux';
import update from 'react-addons-update';
import difference from 'lodash/difference';
import union from 'lodash/union';
import * as Immutable from 'seamless-immutable';
import {buildOxygenObjects} from '../../utils/oxygenHelpers';
import {
  JSONAPI_CONFIGURE,
  ANALYTICS_CONFIGURE,
  OXYGEN_CONFIGURE_ASYNC_PENDING,
  OXYGEN_CONFIGURE_ASYNC_FULFILLED,
  OXYGEN_CONFIGURE_ASYNC_REJECTED,
  OXYGEN_CONFIGURE_USERS_ASYNC_PENDING,
  OXYGEN_CONFIGURE_USERS_ASYNC_FULFILLED,
  OXYGEN_CONFIGURE_USERS_ASYNC_REJECTED,
  DOCUMENT_COLLECTION_CONFIGURE,
  DOCUMENT_CONFIGURE_PENDING,
  DOCUMENT_CONFIGURE_FULFILLED,
  DOCUMENT_CONFIGURE_REJECTED,
  SET_SHEETS_CONFIGURATION,
  LOCATION_TREE_WILL_READ,
  LOCATION_TREE_READ_SUCCESS,
  LOCATION_TREE_READ_FAILED,
  DOCUMENTS_WITH_ISSUES_FETCH,
  ISSUE_TYPES_API_CONFIGURE,
  REQUIRED_FIELDS_CONFIGURE_API,
  ISSUE_ATTRIBUTES_API_CONFIGURE,
  ISSUE_ROOT_CAUSES_API_CONFIGURE,
  DOCUMENT_VERSIONS_API_START,
  DOCUMENT_VERSIONS_API_FULFILLED,
  CHECKLIST_FETCH,
  CHECKLISTS_WITH_ISSUES_FETCH,
  CHECKLISTS_WITH_ISSUES_FETCH_FULFILLED
} from '../actions';

const jsonapiConfigurationReducer = (state = null, action) => {
  switch (action.type) {
    case JSONAPI_CONFIGURE:
      return action.payload;
    default:
      return state;
  }
};

const issueTypesConfigurationReducer = (state = null, action) => {
  switch (action.type) {
    case ISSUE_TYPES_API_CONFIGURE:
      return action.payload;
    default:
      return state;
  }
};

const requiredFieldsConfigurationReducer = (state = null, action) => {
  switch (action.type) {
    case REQUIRED_FIELDS_CONFIGURE_API:
      return action.payload;
    default:
      return state;
  }
};

const issueAttributesConfigurationReducer = (state = null, action) => {
  switch (action.type) {
    case ISSUE_ATTRIBUTES_API_CONFIGURE:
      return action.payload;
    default:
      return state;
  }
};

const issueRootCausesConfigurationReducer = (state = null, action) => {
  switch (action.type) {
    case ISSUE_ROOT_CAUSES_API_CONFIGURE:
      return action.payload;
    default:
      return state;
  }
};

const analyticsConfigurationReducer = (state = null, action) => {
  switch (action.type) {
    case ANALYTICS_CONFIGURE:
      return action.payload;
    default:
      return state;
  }
};

const oxygenConfigurationReducer = (state = null, action) => {
  switch (action.type) {
    case OXYGEN_CONFIGURE_ASYNC_PENDING:
    case OXYGEN_CONFIGURE_ASYNC_REJECTED:
    case OXYGEN_CONFIGURE_USERS_ASYNC_PENDING:
    case OXYGEN_CONFIGURE_USERS_ASYNC_REJECTED:
      return state;
    case OXYGEN_CONFIGURE_ASYNC_FULFILLED:
      return buildOxygenObjects(action.payload, state);
    case OXYGEN_CONFIGURE_USERS_ASYNC_FULFILLED:
      return buildOxygenObjects(action.payload, state);
    default:
      return state;
  }
};

const documentConfigurationReducer = (state = null, action) => {
  switch (action.type) {
    case DOCUMENT_CONFIGURE_PENDING:
    case DOCUMENT_CONFIGURE_REJECTED:
      return state;
    case DOCUMENT_CONFIGURE_FULFILLED:
      return action.payload;
    default:
      return state;
  }
};

const documentCollectionReducer = (state = {}, action) => {
  switch (action.type) {
    case DOCUMENT_COLLECTION_CONFIGURE:
      return {
        ...state,
        ...action.payload
      };
    default:
      return state;
  }
};

const currentlyFetchingDocumentCollectionReducer = (state = [], action) => {
  switch (action.type) {
    case DOCUMENTS_WITH_ISSUES_FETCH:
      return union(state, action.payload);
    case DOCUMENT_COLLECTION_CONFIGURE:
      return difference(state, Object.keys(action.payload));
    default:
      return state;
  }
};

const initialSheetsConfigurationReducerState = {
  sheetList: [],
  currentSheets: []
};

const sheetsConfigurationReducer = (state = initialSheetsConfigurationReducerState, action) => {
  switch (action.type) {
    case SET_SHEETS_CONFIGURATION:
      return action.payload;
    default:
      return state;
  }
};

const initialLocationReducerState = {
  tree: null,
  nodesDictionary: null,
  isFetching: false,
  error: null
};

// Location reducer holds default location tree info for field issues use
const locationReducer = (state = initialLocationReducerState, action) => {
  switch (action.type) {
    case LOCATION_TREE_WILL_READ:
      return update(state, {$merge: {isFetching: true}});
    case LOCATION_TREE_READ_SUCCESS: {
      const {tree, nodesDictionary} = action.payload;
      return update(state, {$merge: {tree, nodesDictionary, isFetching: false, error: null}});
    }
    case LOCATION_TREE_READ_FAILED:
      return update(initialLocationReducerState, {$merge: {error: action.meta.error}});
    default:
      return state;
  }
};
const initialDocumentVersionsState = {
  versions: {},
  fetching: []
};

const documentVersionsReducer = (state = initialDocumentVersionsState, action) => {
  switch (action.type) {
    case DOCUMENT_VERSIONS_API_START:
      return {
        ...state,
        fetching: [...state.fetching, action.payload.urn]
      };
    case DOCUMENT_VERSIONS_API_FULFILLED:
      return {
        ...state,
        versions: {
          ...state.versions,
          [action.payload.urn]: action.payload.versions
        },
        fetching: state.fetching.filter(urn => urn !== action.payload.urn)
      };
    default:
      return state;
  }
};

const initialChecklistState = Immutable.from({instances: {}, sections: {}, items: {}});

const checklistReducer = (state = initialChecklistState, action) => {
  switch (action.type) {
    case CHECKLIST_FETCH:
      return state.merge({
        instances: state.instances.merge(action.payload.instances),
        sections: state.sections.merge(action.payload.sections),
        items: state.items.merge(action.payload.items)
      });

    default:
      return state;
  }
};

const currentlyFetchingChecklistInstances = (state = [], action) => {
  switch (action.type) {
    case CHECKLISTS_WITH_ISSUES_FETCH:
      return union(state, action.payload);
    case CHECKLISTS_WITH_ISSUES_FETCH_FULFILLED:
      return difference(state, action.payload);
    default:
      return state;
  }
};

export default combineReducers({
  jsonapi: jsonapiConfigurationReducer,
  issueTypes: issueTypesConfigurationReducer,
  requiredFields: requiredFieldsConfigurationReducer,
  issueAttributes: issueAttributesConfigurationReducer,
  issueRootCauses: issueRootCausesConfigurationReducer,
  analyticsConfig: analyticsConfigurationReducer,
  oxygen: oxygenConfigurationReducer,
  documentCollection: documentCollectionReducer,
  documentVersions: documentVersionsReducer,
  fetchingIssuesUrns: currentlyFetchingDocumentCollectionReducer,
  document: documentConfigurationReducer,
  sheetsConfig: sheetsConfigurationReducer,
  locationTree: locationReducer,
  checklist: checklistReducer,
  fetchingChecklistInstances: currentlyFetchingChecklistInstances
});
